import { useState, useEffect, useMemo } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { key: "not_started", label: "Not Started", color: "#7A8A96" },
  { key: "intro_sent", label: "Intro Sent", color: "#5AC8D8" },
  { key: "in_progress", label: "Registration In Progress", color: "#FFB347" },
  { key: "submitted", label: "Submitted", color: "#A78BFA" },
  { key: "approved", label: "Approved / Active", color: "#00DC78" }
];

// 14 Agencies + 7 Institutional REITs — sourced from /app/memory/GOVERNMENT_AGENCIES.md + DEPLOYMENT_QUEUE.md
const TARGETS = [
  // ── Tier 1 — Federal Flagships
  { id: "sam_gov", tier: "Federal", priority: "P0", name: "SAM.gov", subtitle: "System for Award Management", url: "https://sam.gov", contact: "(via portal)", note: "Master federal registration. Gateway to all federal contracts. DO FIRST after EIN.", opportunity: "All federal contracts" },
  { id: "fannie_mae", tier: "Federal", priority: "P0", name: "Fannie Mae", subtitle: "ProcureOne — REO Property Preservation", url: "https://procureone.fanniemae.com", contact: "supplier_diversity@fanniemae.com", note: "REO property preservation, make-ready contracts for foreclosed homes. Send intro email NOW (even pre-EIN) to Supplier Diversity.", opportunity: "$25K–$500K per portfolio, multi-year contracts" },
  { id: "freddie_mac", tier: "Federal", priority: "P0", name: "Freddie Mac", subtitle: "Supplier Portal — REO Work", url: "https://suppliers.freddiemac.com", contact: "supplier_diversity@freddiemac.com", note: "Sister GSE to Fannie Mae. Near-identical program. Together they cover entire conventional REO pipeline.", opportunity: "$25K–$500K per portfolio" },
  { id: "hud_section3", tier: "Federal", priority: "P0", name: "HUD Section 3 Business Registry", subtitle: "Hidden Gem — Minority-Owned Priority", url: "https://hudexchange.info/programs/section-3", contact: "(self-certify online)", note: "HUD program that prioritizes minority-owned small businesses for HUD-funded contracts. Once listed, every housing authority receiving HUD funds MUST consider Section 3 businesses first.", opportunity: "Government-required preference" },
  { id: "hud_mm", tier: "Federal", priority: "P2", name: "HUD M&M Contractors Pool", subtitle: "Direct REO Property Management", url: "https://hud.gov/helping-americans/reo-management", contact: "(via SAM)", note: "HUD's direct REO property management program. Requires geographic office presence — revisit month 3-6.", opportunity: "Direct HUD contracts" },
  { id: "va_reo", tier: "Federal", priority: "P1", name: "VA (Veterans Affairs)", subtitle: "REO Property Turnover", url: "https://vendor.va.gov", contact: "(via SAM.gov)", note: "VA-foreclosed home turnover + maintenance. Leverages SAM work already done — small additional effort.", opportunity: "VA REO inventory" },
  { id: "usda_rural", tier: "Federal", priority: "P3", name: "USDA Rural Development", subtitle: "Rural REO Properties", url: "https://sam.gov", contact: "(via SAM.gov)", note: "Rural property REO — Denton/Wise/Parker counties. Lower priority unless serving outlying areas.", opportunity: "Rural REO" },

  // ── Tier 2 — State of Texas
  { id: "tdhca", tier: "State", priority: "P2", name: "TDHCA", subtitle: "Texas Dept. of Housing & Community Affairs", url: "https://tdhca.state.tx.us", contact: "(via portal)", note: "State-funded affordable housing programs. Requires Texas HUB certification (state version of MBE/WBE).", opportunity: "State-funded affordable housing" },

  // ── Tier 3 — Local DFW
  { id: "dha", tier: "Local DFW", priority: "P0", name: "Dallas Housing Authority (DHA)", subtitle: "Hometown Flagship", url: "https://dhantx.com/about-dha/working-with-dha/bids-rfps-and-proposals", contact: "Kenneth Duke — kenneth.duke@dhantx.com | (214) 951-8452 | procurement@dhantx.com", note: "Direct tenant-housing connection, Section 8 property network. Submit packet Week 2 post-EIN. Hometown flagship.", opportunity: "DFW Section 8 property pipeline" },
  { id: "fwhs", tier: "Local DFW", priority: "P0", name: "Fort Worth Housing Solutions", subtitle: "FWHS (formerly FWHA)", url: "https://fwhs.org", contact: "Kathy Bridwell — Procurement | (817) 333-3400", note: "Fort Worth public housing agency. P1 hometown agency. Submit vendor application after EIN.", opportunity: "Fort Worth Section 8" },
  { id: "arlington_ha", tier: "Local DFW", priority: "P1", name: "Arlington Housing Authority", subtitle: "Section 8 Administration", url: "https://arlingtontx.gov", contact: "(via city portal)", note: "Arlington public housing, Section 8 administration. Tier 1-3 of your DFW radius.", opportunity: "Arlington voucher network" },
  { id: "tarrant_county", tier: "Local DFW", priority: "P0", name: "Tarrant County Procurement", subtitle: "Ion Wave Platform", url: "https://ionwave.net", contact: "(via Tarrant County module)", note: "All Tarrant County contracts (covers Fort Worth + Arlington). Registration is free. All county bids flow through here.", opportunity: "All Tarrant County contracts" },
  { id: "dallas_county", tier: "Local DFW", priority: "P0", name: "Dallas County Procurement", subtitle: "BidNet Direct", url: "https://bidnetdirect.com/texas/dallas-county", contact: "(via portal)", note: "Dallas County contracts (all postings since Dec 2024 online). Pair with DHA registration.", opportunity: "All Dallas County contracts" },
  { id: "city_portals", tier: "Local DFW", priority: "P1", name: "City Procurement Portals", subtitle: "Fort Worth, Plano, Arlington, Frisco", url: "https://gobonfire.com", contact: "(per city)", note: "Bonfire platform for Fort Worth + Plano. Direct vendor reg for Arlington + Frisco. Each is a small but recurring contract pool.", opportunity: "Recurring city contracts" },

  // ── Tier 4 — Institutional REITs (private sector institutional)
  { id: "invitation_homes", tier: "Institutional REIT", priority: "P0", name: "Invitation Homes", subtitle: "Largest SFR REIT", url: "https://invitationhomes.com/vendors", contact: "vendors@invitationhomes.com", note: "~80,000 single-family homes. Largest SFR REIT in the U.S. Make-ready volume is enormous.", opportunity: "$25K–$2M+ vendor agreements" },
  { id: "amh", tier: "Institutional REIT", priority: "P0", name: "American Homes 4 Rent (AMH)", subtitle: "Top-3 SFR REIT", url: "https://amh.com/vendors", contact: "vendors@amh.com", note: "~60,000 SFR units. Strong DFW concentration.", opportunity: "$25K–$1.5M vendor agreements" },
  { id: "progress_residential", tier: "Institutional REIT", priority: "P0", name: "Progress Residential", subtitle: "Private SFR Operator", url: "https://rentprogress.com/vendors", contact: "vendors@rentprogress.com", note: "~85,000 SFR units. Private but operates at REIT scale. Strong DFW footprint.", opportunity: "$25K–$1.5M vendor agreements" },
  { id: "tricon", tier: "Institutional REIT", priority: "P1", name: "Tricon Residential", subtitle: "Mid-Market SFR REIT", url: "https://triconresidential.com/vendors", contact: "(via portal)", note: "~38,000 SFR units, growing rapidly in Sun Belt.", opportunity: "$15K–$1M vendor agreements" },
  { id: "fpre", tier: "Institutional REIT", priority: "P1", name: "FirstKey Homes", subtitle: "Cerberus-backed SFR Operator", url: "https://firstkeyhomes.com", contact: "(via portal)", note: "~50,000 SFR units. Cerberus-backed institutional operator.", opportunity: "$15K–$1M vendor agreements" },
  { id: "mid_america", tier: "Institutional REIT", priority: "P2", name: "Mid-America Apartment Communities (MAA)", subtitle: "Sun Belt Multifamily REIT", url: "https://maac.com/vendors", contact: "(via portal)", note: "~100,000 multifamily units across Sun Belt. DFW is a top-3 market.", opportunity: "Multifamily turnover at scale" },
  { id: "camden", tier: "Institutional REIT", priority: "P2", name: "Camden Property Trust", subtitle: "Multifamily REIT (DFW HQ)", url: "https://camdenliving.com/vendors", contact: "(via portal)", note: "DFW-headquartered multifamily REIT. ~58,000 units. Hometown connection.", opportunity: "DFW multifamily turnover" }
];

// Pitch templates by tier
const PITCH_TEMPLATES = {
  "Federal": (target) => `Subject: ROI Stabilization Partner — Moovin Forward LLC | ${target.name} Vendor Inquiry

Dear ${target.name} Supplier Diversity Team,

My name is Shannon, and I am the founder of Moovin Forward LLC, a DFW-based ROI Stabilization Partner specializing in institutional-grade physical asset stabilization for foreclosed and end-of-lease residential inventory. We provide:

• 48-hour Swift Turn capability and 14-21 day full stabilization on legacy units
• NSPIRE, HUD, and HUD-VASH-compliant documentation with receipt-level audit trail
• Locked per-unit pricing — no hidden change orders, no surprise overruns
• Modular, removable upgrade systems that qualify for IRS Section 168(k) accelerated depreciation (Personal Property classification)
• Section 3-eligible minority-owned business positioning

We are in the final stages of EIN issuance and SAM.gov registration, and are reaching out proactively to introduce ourselves to ${target.name}'s Supplier Diversity team. Our DFW market positioning aligns directly with your REO preservation pipeline, and we are positioned to scale alongside your portfolio needs.

I would welcome the opportunity to share a one-page Capabilities document and discuss how Moovin Forward can support ${target.name}'s vendor network. May I send our Capabilities Brief and arrange a 15-minute introductory call?

Respectfully,
Shannon — CEO
Moovin Forward LLC | DFW
hello@moovinforward.org | (817) 381-8831
moovinforward.org

CC: hello@moovinforward.org (audit trail)`,

  "State": (target) => `Subject: Texas HUB-Track ROI Stabilization Partner — Moovin Forward LLC

Dear ${target.name} Procurement Team,

I am writing to introduce Moovin Forward LLC, a DFW-based ROI Stabilization Partner pursuing Texas HUB certification and TDHCA vendor registration to support your state-funded affordable housing programs.

Our focus is institutional-grade physical asset stabilization on legacy residential inventory — NSPIRE-compliant, locked per-unit pricing, receipt-level audit trail. We are particularly suited to support TDHCA's affordable housing portfolio with rapid turnover capability and DFW market knowledge.

I would welcome the opportunity to send our Capabilities Brief and learn what documentation ${target.name} requires for vendor onboarding once HUB certification is finalized.

Respectfully,
Shannon — CEO
Moovin Forward LLC | DFW
hello@moovinforward.org | (817) 381-8831`,

  "Local DFW": (target) => `Subject: DFW-Local ROI Stabilization Partner — Moovin Forward LLC | ${target.name} Vendor Inquiry

Dear ${target.name} Procurement Team${target.contact && target.contact.includes("Kenneth Duke") ? " (attn: Kenneth Duke)" : target.contact && target.contact.includes("Kathy Bridwell") ? " (attn: Kathy Bridwell)" : ""},

My name is Shannon, founder of Moovin Forward LLC — a DFW-based, minority-owned ROI Stabilization Partner specializing in Section 8 / HUD-compliant physical asset stabilization for residential properties.

What sets us apart for ${target.name}'s pipeline:
• 48-hour Swift Turn — vacancy-to-leased timelines that protect your housing voucher utilization rate
• NSPIRE + HQS pre-inspection capability — units pass first inspection, no costly re-inspections
• Locked per-unit pricing with line-itemized capital deployment (labor / cleaning / materials / oversight separated for clean audit trails)
• Section 3 Business positioning (minority-owned, DFW-headquartered)
• Receipt-level documentation for every job — no lump-sum invoices

We are completing our EIN registration and finalizing our packet for ${target.name}'s vendor onboarding. I would welcome the chance to deliver our packet in person, drop off our Capabilities Brief, or schedule a 15-minute introductory call at your convenience.

Respectfully,
Shannon — CEO
Moovin Forward LLC | DFW
hello@moovinforward.org | (817) 381-8831
moovinforward.org`,

  "Institutional REIT": (target) => `Subject: ROI Stabilization Partner — Moovin Forward LLC | ${target.name} Vendor Inquiry

Dear ${target.name} Vendor Onboarding Team,

I am the founder of Moovin Forward LLC, a DFW-based ROI Stabilization Partner. We approach single-family and multifamily turnover differently than the typical make-ready vendor — we engineer the unit for maximum revenue restoration AND maximum tax efficiency for the owner.

Three differentiators for ${target.name}'s portfolio managers:

1. SCOPE-LOCKED PRICING — Every project begins with a signed Scope-Lock Agreement at 20% markup. No hidden fees, no surprise change orders. Your CFO sees the full project cost on Day 1.

2. SECTION 168(k) TAX SHIELD — Our modular stabilization system is interchangeable and removable, classified by the IRS as Personal Property (5-year asset life) rather than Real Property (27.5-year life). On a $10,000 unit upgrade, owners can typically realize $2,000–$3,500 in Year-1 tax savings under Section 168(k) bonus depreciation. Receipt-level audit trail included — your CPA gets a Cost Segregation-ready package, not a lump-sum invoice. (Estimates only — not tax advice. Consult your CPA.)

3. SWIFT TURN + AUDIT TRAIL — 48-hour Swift Turn capability. 14-21 day full stabilization. NSPIRE, HUD, HUD-VASH, and City Code compliant documentation on every job.

I would welcome the opportunity to send our one-page Capabilities Brief and a sample Scope-Lock Agreement. May I arrange a 15-minute introductory call with your DFW portfolio team?

Respectfully,
Shannon — CEO
Moovin Forward LLC | DFW ROI Stabilization Firm
hello@moovinforward.org | (817) 381-8831
moovinforward.org`
};

const STORAGE_KEY = "mf_pitch_center_status_v1";

const PitchCenterTab = () => {
  const [statuses, setStatuses] = useState({});
  const [tierFilter, setTierFilter] = useState("All");
  const [activePitch, setActivePitch] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [seedStatus, setSeedStatus] = useState(""); // "", "seeding", "done"
  const [seedResult, setSeedResult] = useState(null);

  const downloadBankerBriefing = () => {
    window.open(`${BACKEND_URL}/api/admin/bank-pitch-pdf`, "_blank");
  };

  const downloadUnderwriterPacket = () => {
    window.open(`${BACKEND_URL}/api/admin/bank-underwriter-packet`, "_blank");
  };

  const downloadMaterialsManifest = (nspireOnly) => {
    const q = nspireOnly ? "?nspire_only=true" : "";
    window.open(`${BACKEND_URL}/api/admin/materials-manifest-pdf${q}`, "_blank");
  };

  // ── Pre-Engagement Letter (project-scoped, credit-smart, no LLM) ──
  const [pelOpen, setPelOpen] = useState(false);
  const [pelProjects, setPelProjects] = useState([]);
  const [pelProjectsLoading, setPelProjectsLoading] = useState(false);
  const [pelProjectId, setPelProjectId] = useState("");
  const [pelRecipientName, setPelRecipientName] = useState("");
  const [pelRecipientEntity, setPelRecipientEntity] = useState("");
  const [pelRecipientRole, setPelRecipientRole] = useState("");
  const [pelRecipientEmail, setPelRecipientEmail] = useState("");
  const [pelNote, setPelNote] = useState("");
  const [pelNspireOnly, setPelNspireOnly] = useState(true);
  const [pelError, setPelError] = useState("");
  const [pelSending, setPelSending] = useState(false);
  const [pelSendResult, setPelSendResult] = useState(null);

  const openPelModal = async () => {
    setPelOpen(true);
    setPelError("");
    if (pelProjects.length === 0) {
      setPelProjectsLoading(true);
      try {
        const r = await fetch(`${BACKEND_URL}/api/admin/projects`, { credentials: "include" });
        if (r.ok) {
          const list = await r.json();
          setPelProjects(Array.isArray(list) ? list : []);
        } else {
          setPelError("Could not load projects.");
        }
      } catch (e) {
        setPelError("Network error loading projects.");
      }
      setPelProjectsLoading(false);
    }
  };

  const closePelModal = () => {
    setPelOpen(false);
    setPelError("");
    setPelSendResult(null);
  };

  // ── Pre-Engagement Letter Send Log (last N sends + tracking) ──
  const [pelSendLog, setPelSendLog] = useState([]);
  const [pelLogOpen, setPelLogOpen] = useState(true);

  const loadPelSendLog = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/pre-engagement-letter/sends?limit=10`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setPelSendLog(d.sends || []);
      }
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    loadPelSendLog();
  }, []);

  const generatePel = () => {
    if (!pelProjectId) {
      setPelError("Pick a project first.");
      return;
    }
    const params = new URLSearchParams({
      project_id: pelProjectId,
      recipient_name: pelRecipientName.trim(),
      recipient_entity: pelRecipientEntity.trim(),
      recipient_role: pelRecipientRole.trim(),
      nspire_only: pelNspireOnly ? "true" : "false",
    });
    window.open(`${BACKEND_URL}/api/admin/pre-engagement-letter?${params.toString()}`, "_blank");
  };

  const emailPel = async () => {
    if (!pelProjectId) {
      setPelError("Pick a project first.");
      return;
    }
    const email = pelRecipientEmail.trim();
    if (!email || !email.includes("@")) {
      setPelError("Enter a valid recipient email to send.");
      return;
    }
    setPelError("");
    setPelSendResult(null);
    setPelSending(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/pre-engagement-letter/email`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project_id: pelProjectId,
          recipient_email: email,
          recipient_name: pelRecipientName.trim(),
          recipient_entity: pelRecipientEntity.trim(),
          recipient_role: pelRecipientRole.trim(),
          nspire_only: pelNspireOnly,
          note: pelNote.trim(),
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        setPelSendResult({ ok: true, sent_to: d.sent_to, pdf_bytes: d.pdf_bytes });
      } else {
        setPelError(d.detail || "Email send failed.");
      }
    } catch (e) {
      setPelError("Network error. Try again.");
    }
    setPelSending(false);
  };

  const seedBankerScripts = async () => {
    if (seedStatus === "seeding") return;
    setSeedStatus("seeding");
    setSeedResult(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/ai/scripts/seed-banker-pack`, {
        method: "POST", credentials: "include"
      });
      const d = await r.json();
      if (r.ok) {
        setSeedResult(d);
        setSeedStatus("done");
        setTimeout(() => setSeedStatus(""), 6000);
      } else {
        setSeedResult({ error: d.detail || "Could not seed scripts." });
        setSeedStatus("done");
      }
    } catch (e) {
      setSeedResult({ error: "Network error. Try again." });
      setSeedStatus("done");
    }
  };

  // Load saved statuses
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      setStatuses(stored);
    } catch {
      setStatuses({});
    }
  }, []);

  // Alt-NDA queue (institutions that uploaded their own NDA awaiting review)
  const [altRequests, setAltRequests] = useState([]);
  const [altLoading, setAltLoading] = useState(false);
  const [altDecideId, setAltDecideId] = useState(null);
  const [altDecideNote, setAltDecideNote] = useState("");

  const loadAltRequests = async () => {
    setAltLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/alt-nda-requests?status_filter=pending_review`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setAltRequests(d.requests || []);
      }
    } catch (e) { /* silent */ }
    setAltLoading(false);
  };

  useEffect(() => { loadAltRequests(); }, []);

  const decideAlt = async (request_id, decision) => {
    if (!window.confirm(`${decision === "approve" ? "Approve" : "Decline"} this alt-NDA request?\n\nThe recipient will be emailed automatically.`)) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/alt-nda-requests/${request_id}/${decision}`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ decision_note: altDecideNote }),
      });
      if (r.ok) {
        setAltRequests(prev => prev.filter(x => x.request_id !== request_id));
        setAltDecideId(null);
        setAltDecideNote("");
      } else {
        const d = await r.json().catch(() => ({}));
        alert(d.detail || "Action failed.");
      }
    } catch (e) {
      alert("Network error. Try again.");
    }
  };

  const viewAltNdaFile = (request_id) => {
    window.open(`${BACKEND_URL}/api/admin/alt-nda-requests/${request_id}/nda-file`, "_blank");
  };

  // Persist statuses
  const updateStatus = (id, newStatus) => {
    const updated = { ...statuses, [id]: { status: newStatus, updated: new Date().toISOString() } };
    setStatuses(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  };

  // Counts
  const counts = useMemo(() => {
    const c = { total: TARGETS.length, not_started: 0, intro_sent: 0, in_progress: 0, submitted: 0, approved: 0 };
    TARGETS.forEach(t => {
      const s = statuses[t.id]?.status || "not_started";
      c[s] = (c[s] || 0) + 1;
    });
    return c;
  }, [statuses]);

  const tiers = ["All", "Federal", "State", "Local DFW", "Institutional REIT"];
  const visible = tierFilter === "All" ? TARGETS : TARGETS.filter(t => t.tier === tierFilter);

  const generatePitch = (target) => {
    const template = PITCH_TEMPLATES[target.tier] || PITCH_TEMPLATES["Local DFW"];
    return template(target);
  };

  const copyToClipboard = async (target) => {
    const pitch = generatePitch(target);
    try {
      await navigator.clipboard.writeText(pitch);
      setCopiedId(target.id);
      setTimeout(() => setCopiedId(null), 2200);
    } catch {
      // Fallback
      const ta = document.createElement("textarea");
      ta.value = pitch;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopiedId(target.id);
      setTimeout(() => setCopiedId(null), 2200);
    }
  };

  const openMailto = (target) => {
    const pitch = generatePitch(target);
    const lines = pitch.split("\n");
    const subject = lines[0].replace(/^Subject:\s*/i, "");
    const body = lines.slice(1).join("\n").trim();
    const to = (target.contact || "").match(/[\w.+-]+@[\w-]+\.[\w.-]+/);
    const toAddr = to ? to[0] : "";
    const url = `mailto:${toAddr}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}&bcc=hello@moovinforward.org`;
    window.location.href = url;
    if (statuses[target.id]?.status !== "approved") {
      updateStatus(target.id, "intro_sent");
    }
  };

  return (
    <div className="dash-tab-content pitch-center-tab" data-testid="pitch-center-tab">
      <div className="pitch-header">
        <div>
          <h2>Government & Enterprise Pitch Center</h2>
          <p className="pitch-subtitle">14 agencies + 7 institutional REITs. One-click intro emails. Status tracking on every target.</p>
        </div>
      </div>

      {/* Banker Toolkit — Jill-Cushman tier for lenders, REO managers, Special Asset Groups */}
      <div
        data-testid="banker-toolkit"
        style={{
          background: "linear-gradient(135deg, rgba(27, 42, 78, 0.18) 0%, rgba(184, 145, 90, 0.08) 100%)",
          border: "1px solid rgba(184, 145, 90, 0.3)",
          borderLeft: "4px solid #B8915A",
          borderRadius: 8,
          padding: "1.1rem 1.3rem",
          margin: "0.8rem 0 1.4rem 0",
          display: "flex",
          gap: "1.2rem",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: "1 1 320px", minWidth: 280 }}>
          <div style={{ fontSize: "0.7rem", letterSpacing: "0.18em", textTransform: "uppercase", color: "#B8915A", fontWeight: 700, marginBottom: 4 }}>
            🏦 Banker Toolkit — Jill-Cushman Tier
          </div>
          <div style={{ color: "#E5E7EB", fontWeight: 600, fontSize: "0.98rem", lineHeight: 1.4 }}>
            How bankers talk + what they're looking for.
          </div>
          <div style={{ color: "#9CA3AF", fontSize: "0.83rem", marginTop: 4, lineHeight: 1.5 }}>
            Two-page institutional briefing (OCC 2013-29 vendor risk, REO velocity, mechanic's-lien firewall, LGD predictability),
            4 banker-language scripts (REO cold email, LinkedIn DM, voicemail, follow-up),
            and the full <strong style={{ color: "#86EFAC" }}>Underwriter Packet</strong> (7 sections + 22-clause Master Vendor Services Agreement) so their underwriter has nothing left to ask.
          </div>
        </div>
        <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={downloadBankerBriefing}
            data-testid="banker-briefing-download"
            style={{
              background: "#1b2a4e",
              color: "#f5efe1",
              border: "1px solid #1b2a4e",
              padding: "0.7rem 1.2rem",
              borderRadius: 6,
              fontWeight: 700,
              fontSize: "0.88rem",
              letterSpacing: "0.03em",
              cursor: "pointer",
              transition: "transform 0.15s ease, box-shadow 0.2s ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 6px 14px rgba(27,42,78,0.35)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
          >
            🏦 Banker Briefing PDF
          </button>
          <button
            type="button"
            onClick={downloadUnderwriterPacket}
            data-testid="underwriter-packet-download"
            style={{
              background: "#7a8f7e",
              color: "#f5efe1",
              border: "1px solid #5e7263",
              padding: "0.7rem 1.2rem",
              borderRadius: 6,
              fontWeight: 700,
              fontSize: "0.88rem",
              letterSpacing: "0.03em",
              cursor: "pointer",
              transition: "transform 0.15s ease, box-shadow 0.2s ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 6px 14px rgba(94,114,99,0.4)"; e.currentTarget.style.background = "#5e7263"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.background = "#7a8f7e"; }}
          >
            🛡️ Underwriter Packet
          </button>
          <button
            type="button"
            onClick={() => downloadMaterialsManifest(true)}
            data-testid="materials-manifest-download"
            style={{
              background: "transparent",
              color: "#86EFAC",
              border: "1px solid #5e7263",
              padding: "0.7rem 1.2rem",
              borderRadius: 6,
              fontWeight: 700,
              fontSize: "0.88rem",
              letterSpacing: "0.03em",
              cursor: "pointer",
              transition: "background 0.2s ease, color 0.2s ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#5e7263"; e.currentTarget.style.color = "#f5efe1"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#86EFAC"; }}
          >
            📋 Materials Manifest
          </button>
          <button
            type="button"
            onClick={openPelModal}
            data-testid="pre-engagement-letter-open"
            style={{
              background: "#B8915A",
              color: "#1b2a4e",
              border: "1px solid #8a6c3e",
              padding: "0.7rem 1.2rem",
              borderRadius: 6,
              fontWeight: 700,
              fontSize: "0.88rem",
              letterSpacing: "0.03em",
              cursor: "pointer",
              transition: "transform 0.15s ease, box-shadow 0.2s ease",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 6px 14px rgba(184,145,90,0.4)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
          >
            📝 Pre-Engagement Letter
          </button>
          <button
            type="button"
            onClick={seedBankerScripts}
            disabled={seedStatus === "seeding"}
            data-testid="banker-scripts-seed"
            style={{
              background: "transparent",
              color: "#B8915A",
              border: "1px solid #B8915A",
              padding: "0.7rem 1.2rem",
              borderRadius: 6,
              fontWeight: 700,
              fontSize: "0.88rem",
              letterSpacing: "0.03em",
              cursor: seedStatus === "seeding" ? "not-allowed" : "pointer",
              opacity: seedStatus === "seeding" ? 0.6 : 1,
              transition: "background 0.2s ease, color 0.2s ease",
            }}
            onMouseEnter={(e) => { if (seedStatus !== "seeding") { e.currentTarget.style.background = "#B8915A"; e.currentTarget.style.color = "#1b2a4e"; } }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#B8915A"; }}
          >
            {seedStatus === "seeding" ? "Adding…" : "💬 Seed Banker Scripts (4)"}
          </button>
        </div>
        {seedResult && seedStatus === "done" && (
          <div
            data-testid="banker-seed-result"
            style={{
              flex: "1 0 100%",
              background: seedResult.error ? "rgba(220, 38, 38, 0.12)" : "rgba(34, 197, 94, 0.12)",
              border: `1px solid ${seedResult.error ? "rgba(220, 38, 38, 0.4)" : "rgba(34, 197, 94, 0.4)"}`,
              color: seedResult.error ? "#FCA5A5" : "#86EFAC",
              padding: "0.65rem 0.9rem",
              borderRadius: 6,
              fontSize: "0.85rem",
              marginTop: "0.4rem",
            }}
          >
            {seedResult.error ? (
              <>⚠ {seedResult.error}</>
            ) : (
              <>
                ✓ Added <strong>{seedResult.added_count}</strong> new banker script{seedResult.added_count === 1 ? "" : "s"} to your <em>Scripts</em> tab
                {seedResult.skipped_count > 0 && <> · <strong>{seedResult.skipped_count}</strong> already in library</>}.
                {seedResult.added && seedResult.added.length > 0 && (
                  <div style={{ marginTop: 4, fontSize: "0.78rem", opacity: 0.85 }}>{seedResult.added.join(" · ")}</div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Pre-Engagement Letter Modal — project-scoped, credit-smart */}
      {pelOpen && (
        <div
          data-testid="pre-engagement-letter-modal"
          onClick={(e) => { if (e.target === e.currentTarget) closePelModal(); }}
          style={{
            position: "fixed", inset: 0, background: "rgba(8, 13, 28, 0.78)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 9000, padding: "1rem",
          }}
        >
          <div
            style={{
              background: "#0f172a",
              border: "1px solid rgba(184, 145, 90, 0.4)",
              borderLeft: "4px solid #B8915A",
              borderRadius: 10,
              padding: "1.4rem 1.6rem",
              width: "100%", maxWidth: 560,
              boxShadow: "0 28px 60px rgba(0,0,0,0.5)",
              maxHeight: "92vh", overflowY: "auto",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.6rem", marginBottom: "0.9rem" }}>
              <div>
                <div style={{ fontSize: "0.66rem", letterSpacing: "0.18em", textTransform: "uppercase", color: "#B8915A", fontWeight: 700 }}>
                  📝 Pre-Engagement Spec Letter
                </div>
                <div style={{ color: "#F3F4F6", fontWeight: 700, fontSize: "1.05rem", marginTop: 4 }}>
                  Project-scoped 1-pager + NSPIRE-safe exhibit
                </div>
                <div style={{ color: "#9CA3AF", fontSize: "0.82rem", marginTop: 4, lineHeight: 1.5 }}>
                  Hand this to a contracting officer / asset manager BEFORE the engagement letter. Credit-smart: no LLM calls, pure template.
                </div>
              </div>
              <button type="button" onClick={closePelModal} data-testid="pre-engagement-letter-close"
                      style={{ background: "transparent", border: "none", color: "#9CA3AF", fontSize: "1.4rem", cursor: "pointer", lineHeight: 1 }}>
                ×
              </button>
            </div>

            <div style={{ display: "grid", gap: "0.85rem" }}>
              <label style={{ display: "block" }}>
                <div style={{ color: "#D1D5DB", fontSize: "0.78rem", fontWeight: 700, marginBottom: 4 }}>Project *</div>
                <select
                  value={pelProjectId}
                  onChange={(e) => setPelProjectId(e.target.value)}
                  disabled={pelProjectsLoading}
                  data-testid="pre-engagement-letter-project"
                  style={{ width: "100%", background: "#1f2937", border: "1px solid #374151", color: "#F3F4F6", padding: "0.6rem 0.7rem", borderRadius: 5, fontSize: "0.92rem" }}
                >
                  <option value="">{pelProjectsLoading ? "Loading projects…" : "— Pick a project —"}</option>
                  {pelProjects.map(p => (
                    <option key={p.project_id} value={p.project_id}>
                      {(p.address || "[no address]")}{p.client_name ? ` · ${p.client_name}` : ""}{p.status ? ` · ${p.status}` : ""}
                    </option>
                  ))}
                </select>
              </label>

              <div style={{ background: "rgba(184, 145, 90, 0.06)", border: "1px solid rgba(184, 145, 90, 0.18)", borderRadius: 6, padding: "0.7rem 0.85rem" }}>
                <div style={{ color: "#B8915A", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 6 }}>
                  Addressee (optional)
                </div>
                <div style={{ color: "#9CA3AF", fontSize: "0.76rem", marginBottom: 8, lineHeight: 1.45 }}>
                  Leave blank for "To Whom It May Concern." Otherwise the letter is personalized.
                </div>
                <input
                  type="text" placeholder="Recipient legal name"
                  value={pelRecipientName} onChange={(e) => setPelRecipientName(e.target.value)}
                  data-testid="pre-engagement-letter-recipient-name"
                  style={{ width: "100%", background: "#1f2937", border: "1px solid #374151", color: "#F3F4F6", padding: "0.55rem 0.7rem", borderRadius: 5, fontSize: "0.9rem", marginBottom: 6 }}
                />
                <input
                  type="text" placeholder="Recipient role / title"
                  value={pelRecipientRole} onChange={(e) => setPelRecipientRole(e.target.value)}
                  data-testid="pre-engagement-letter-recipient-role"
                  style={{ width: "100%", background: "#1f2937", border: "1px solid #374151", color: "#F3F4F6", padding: "0.55rem 0.7rem", borderRadius: 5, fontSize: "0.9rem", marginBottom: 6 }}
                />
                <input
                  type="text" placeholder="Entity / institution"
                  value={pelRecipientEntity} onChange={(e) => setPelRecipientEntity(e.target.value)}
                  data-testid="pre-engagement-letter-recipient-entity"
                  style={{ width: "100%", background: "#1f2937", border: "1px solid #374151", color: "#F3F4F6", padding: "0.55rem 0.7rem", borderRadius: 5, fontSize: "0.9rem", marginBottom: 6 }}
                />
                <input
                  type="email" placeholder="Recipient email (required to send)"
                  value={pelRecipientEmail} onChange={(e) => setPelRecipientEmail(e.target.value)}
                  data-testid="pre-engagement-letter-recipient-email"
                  style={{ width: "100%", background: "#1f2937", border: "1px solid #374151", color: "#F3F4F6", padding: "0.55rem 0.7rem", borderRadius: 5, fontSize: "0.9rem", marginBottom: 6 }}
                />
                <textarea
                  placeholder="Optional personal note (one short paragraph — appears above the attachment in the email body)"
                  value={pelNote} onChange={(e) => setPelNote(e.target.value)}
                  data-testid="pre-engagement-letter-note"
                  rows={2}
                  style={{ width: "100%", background: "#1f2937", border: "1px solid #374151", color: "#F3F4F6", padding: "0.55rem 0.7rem", borderRadius: 5, fontSize: "0.86rem", resize: "vertical", fontFamily: "inherit" }}
                />
              </div>

              <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "#D1D5DB", fontSize: "0.85rem", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={pelNspireOnly}
                  onChange={(e) => setPelNspireOnly(e.target.checked)}
                  data-testid="pre-engagement-letter-nspire-only"
                />
                <span>NSPIRE-safe materials only (Class A) — recommended for institutional handoffs</span>
              </label>

              {pelError && (
                <div data-testid="pre-engagement-letter-error"
                     style={{ background: "rgba(220, 38, 38, 0.12)", border: "1px solid rgba(220, 38, 38, 0.4)", color: "#FCA5A5", padding: "0.55rem 0.8rem", borderRadius: 5, fontSize: "0.82rem" }}>
                  ⚠ {pelError}
                </div>
              )}

              {pelSendResult && pelSendResult.ok && (
                <div data-testid="pre-engagement-letter-sent"
                     style={{ background: "rgba(34, 197, 94, 0.12)", border: "1px solid rgba(34, 197, 94, 0.4)", color: "#86EFAC", padding: "0.6rem 0.9rem", borderRadius: 5, fontSize: "0.84rem", lineHeight: 1.5 }}>
                  ✓ Letter emailed to <strong>{pelSendResult.sent_to}</strong> ({(pelSendResult.pdf_bytes / 1024).toFixed(1)} KB PDF attached).
                  {pelSendResult.sent_to !== pelRecipientEmail.trim() && (
                    <div style={{ marginTop: 4, fontSize: "0.76rem", opacity: 0.85 }}>
                      🟡 Sandbox mode active — routed to Shannon for review instead of the recipient.
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: "flex", gap: "0.6rem", justifyContent: "flex-end", marginTop: 4, flexWrap: "wrap" }}>
                <button type="button" onClick={closePelModal}
                        data-testid="pre-engagement-letter-cancel"
                        style={{ background: "transparent", color: "#9CA3AF", border: "1px solid #4B5563", padding: "0.6rem 1.1rem", borderRadius: 5, fontWeight: 600, fontSize: "0.85rem", cursor: "pointer" }}>
                  Cancel
                </button>
                <button type="button" onClick={generatePel}
                        disabled={!pelProjectId}
                        data-testid="pre-engagement-letter-generate"
                        style={{
                          background: pelProjectId ? "transparent" : "#4B5563",
                          color: pelProjectId ? "#B8915A" : "#9CA3AF",
                          border: "1px solid " + (pelProjectId ? "#B8915A" : "#4B5563"),
                          padding: "0.6rem 1.1rem",
                          borderRadius: 5,
                          fontWeight: 700,
                          fontSize: "0.88rem",
                          cursor: pelProjectId ? "pointer" : "not-allowed",
                        }}>
                  📝 Download PDF
                </button>
                <button type="button" onClick={emailPel}
                        disabled={!pelProjectId || pelSending}
                        data-testid="pre-engagement-letter-email-send"
                        style={{
                          background: pelProjectId && !pelSending ? "#B8915A" : "#4B5563",
                          color: pelProjectId && !pelSending ? "#1b2a4e" : "#9CA3AF",
                          border: "1px solid " + (pelProjectId && !pelSending ? "#8a6c3e" : "#4B5563"),
                          padding: "0.6rem 1.3rem",
                          borderRadius: 5,
                          fontWeight: 700,
                          fontSize: "0.9rem",
                          cursor: pelProjectId && !pelSending ? "pointer" : "not-allowed",
                          opacity: pelSending ? 0.7 : 1,
                        }}>
                  {pelSending ? "Sending…" : "📧 Email to Recipient"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pre-Engagement Letter Send Log */}
      <div data-testid="pel-send-log" style={{
        background: "rgba(15,23,42,0.55)", border: "1px solid rgba(184,145,90,0.25)",
        borderLeft: "4px solid #B8915A", borderRadius: 8, padding: "1rem 1.2rem"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
             onClick={() => setPelLogOpen(!pelLogOpen)}>
          <div>
            <div style={{ fontSize: "0.7rem", letterSpacing: "0.16em", textTransform: "uppercase", color: "#B8915A", fontWeight: 700 }}>
              📬 Pre-Engagement Letter Log
            </div>
            <div style={{ color: "#F3F4F6", fontWeight: 700, fontSize: "1rem", marginTop: 2 }}>
              Last {pelSendLog.length} send{pelSendLog.length === 1 ? "" : "s"} {pelSendLog.filter(s => (s.tracking||{}).opened_at).length > 0 ? ` · ${pelSendLog.filter(s => (s.tracking||{}).opened_at).length} opened` : ""}
            </div>
          </div>
          <button type="button" style={{ background: "transparent", border: "1px solid #B8915A", color: "#B8915A", padding: "0.35rem 0.75rem", borderRadius: 4, fontWeight: 700, fontSize: "0.78rem", cursor: "pointer" }}>
            {pelLogOpen ? "Hide" : "Show"}
          </button>
        </div>
        {pelLogOpen && (
          <div style={{ marginTop: "0.7rem" }}>
            {pelSendLog.length === 0 && (
              <div style={{ color: "#6B7280", fontStyle: "italic", fontSize: "0.84rem", padding: "0.5rem 0" }}>
                No pre-engagement letters sent yet. Hit the 📝 Pre-Engagement Letter button above to send your first.
              </div>
            )}
            {pelSendLog.map(s => {
              const t = s.tracking || {};
              const statusKey = t.status || "sent";
              const statusColor = {
                sent: "#9CA3AF", delivered: "#5AC8D8", opened: "#FFB347",
                clicked: "#00DC78", bounced: "#FCA5A5"
              }[statusKey] || "#9CA3AF";
              const statusLabel = {
                sent: "📤 Sent", delivered: "📬 Delivered", opened: "👁️ Opened",
                clicked: "🖱️ Clicked", bounced: "⚠️ Bounced"
              }[statusKey] || "📤 Sent";
              return (
                <div key={s.send_id} data-testid={`pel-log-row-${s.send_id}`} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                  padding: "0.55rem 0.7rem", borderTop: "1px solid rgba(255,255,255,0.06)",
                  gap: "0.6rem", flexWrap: "wrap"
                }}>
                  <div style={{ flex: "1 1 280px", minWidth: 220 }}>
                    <div style={{ color: "#F3F4F6", fontWeight: 600, fontSize: "0.88rem" }}>
                      {s.project_address || "Project"}
                    </div>
                    <div style={{ color: "#9CA3AF", fontSize: "0.78rem", marginTop: 2 }}>
                      To: <strong style={{ color: "#D1D5DB" }}>{s.recipient_name || "—"}</strong>
                      {s.recipient_entity ? ` · ${s.recipient_entity}` : ""} · {s.recipient_email}
                    </div>
                    <div style={{ color: "#6B7280", fontSize: "0.72rem", marginTop: 2 }}>
                      Sent {s.sent_at?.slice(0,16).replace("T"," ")} UTC by {s.sent_by || "—"}
                    </div>
                  </div>
                  <div style={{ padding: "0.28rem 0.65rem", borderRadius: 999, border: `1px solid ${statusColor}`, color: statusColor, fontSize: "0.74rem", fontWeight: 700, whiteSpace: "nowrap" }}>
                    {statusLabel}{t.open_count > 1 ? ` (${t.open_count}x)` : ""}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Alt-NDA Review Queue — institutions that uploaded their own NDA */}
      {(altRequests.length > 0 || altLoading) && (
        <div
          data-testid="alt-nda-queue"
          style={{
            background: "rgba(122, 143, 126, 0.12)",
            border: "1px solid rgba(122, 143, 126, 0.4)",
            borderLeft: "4px solid #7a8f7e",
            borderRadius: 8,
            padding: "1.1rem 1.3rem",
            margin: "0 0 1.4rem 0",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.8rem", flexWrap: "wrap", gap: "0.6rem" }}>
            <div>
              <div style={{ fontSize: "0.7rem", letterSpacing: "0.18em", textTransform: "uppercase", color: "#86EFAC", fontWeight: 700, marginBottom: 2 }}>
                📥 Alt-NDA Review Queue
              </div>
              <div style={{ color: "#E5E7EB", fontWeight: 600, fontSize: "0.96rem" }}>
                {altRequests.length} institution{altRequests.length === 1 ? "" : "s"} uploaded their own NDA. Review and decide.
              </div>
            </div>
            <button type="button" onClick={loadAltRequests} disabled={altLoading}
                    data-testid="alt-nda-refresh"
                    style={{ background: "transparent", border: "1px solid #86EFAC", color: "#86EFAC", padding: "0.4rem 0.9rem", borderRadius: 4, fontWeight: 700, fontSize: "0.78rem", cursor: "pointer" }}>
              {altLoading ? "Refreshing…" : "↻ Refresh"}
            </button>
          </div>
          <div style={{ display: "grid", gap: "0.7rem" }}>
            {altRequests.map(req => (
              <div key={req.request_id}
                   data-testid={`alt-nda-row-${req.request_id}`}
                   style={{ background: "rgba(15, 23, 42, 0.5)", borderRadius: 6, padding: "0.85rem 1rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "0.6rem", marginBottom: "0.5rem" }}>
                  <div>
                    <div style={{ color: "#F3F4F6", fontWeight: 700, fontSize: "0.96rem" }}>
                      {req.legal_name} <span style={{ color: "#9CA3AF", fontWeight: 400 }}>·</span> {req.entity}
                    </div>
                    <div style={{ color: "#9CA3AF", fontSize: "0.8rem", marginTop: 2 }}>
                      {req.role || "—"} · {(req.tier || "").replace(/_/g, " ")} · {req.email}
                    </div>
                    <div style={{ color: "#6B7280", fontSize: "0.74rem", marginTop: 2 }}>
                      Packet: <strong>{req.packet}</strong> · NDA: {req.entity_nda_title || req.nda_file_name} · Submitted {req.submitted_at?.slice(0, 16)}
                    </div>
                    {req.placement_context && (
                      <div style={{ color: "#D1D5DB", fontSize: "0.8rem", marginTop: 6, fontStyle: "italic", borderLeft: "2px solid #6B7280", paddingLeft: 8 }}>
                        "{req.placement_context}"
                      </div>
                    )}
                  </div>
                </div>
                {altDecideId === req.request_id ? (
                  <div style={{ marginTop: "0.5rem" }}>
                    <textarea
                      value={altDecideNote}
                      onChange={(e) => setAltDecideNote(e.target.value)}
                      placeholder="Optional note to the recipient…"
                      data-testid={`alt-nda-note-${req.request_id}`}
                      style={{ width: "100%", padding: "0.5rem", background: "rgba(255,255,255,0.06)", border: "1px solid #4B5563", borderRadius: 4, color: "#F3F4F6", fontSize: "0.85rem", minHeight: 60, fontFamily: "inherit" }}
                    />
                    <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem", flexWrap: "wrap" }}>
                      <button type="button" onClick={() => decideAlt(req.request_id, "approve")}
                              data-testid={`alt-nda-confirm-approve-${req.request_id}`}
                              style={{ background: "#16a34a", color: "#fff", border: "none", padding: "0.5rem 1rem", borderRadius: 4, fontWeight: 700, fontSize: "0.82rem", cursor: "pointer" }}>
                        ✓ Approve & Release Packet
                      </button>
                      <button type="button" onClick={() => decideAlt(req.request_id, "decline")}
                              data-testid={`alt-nda-confirm-decline-${req.request_id}`}
                              style={{ background: "#dc2626", color: "#fff", border: "none", padding: "0.5rem 1rem", borderRadius: 4, fontWeight: 700, fontSize: "0.82rem", cursor: "pointer" }}>
                        ✗ Decline
                      </button>
                      <button type="button" onClick={() => { setAltDecideId(null); setAltDecideNote(""); }}
                              style={{ background: "transparent", color: "#9CA3AF", border: "1px solid #4B5563", padding: "0.5rem 1rem", borderRadius: 4, fontWeight: 600, fontSize: "0.82rem", cursor: "pointer" }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.4rem", flexWrap: "wrap" }}>
                    <button type="button" onClick={() => viewAltNdaFile(req.request_id)}
                            data-testid={`alt-nda-view-${req.request_id}`}
                            style={{ background: "transparent", color: "#86EFAC", border: "1px solid #86EFAC", padding: "0.4rem 0.85rem", borderRadius: 4, fontWeight: 700, fontSize: "0.78rem", cursor: "pointer" }}>
                      👁 View Their NDA
                    </button>
                    <button type="button" onClick={() => { setAltDecideId(req.request_id); setAltDecideNote(""); }}
                            data-testid={`alt-nda-decide-${req.request_id}`}
                            style={{ background: "#7a8f7e", color: "#1b2a4e", border: "none", padding: "0.4rem 0.85rem", borderRadius: 4, fontWeight: 700, fontSize: "0.78rem", cursor: "pointer" }}>
                      Decide →
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Status Summary Bar */}
      <div className="pitch-summary-bar" data-testid="pitch-summary">
        <div className="pitch-summary-stat">
          <span className="pitch-stat-val">{counts.total}</span>
          <span className="pitch-stat-lbl">Total Targets</span>
        </div>
        <div className="pitch-summary-stat" style={{ color: "#5AC8D8" }}>
          <span className="pitch-stat-val">{counts.intro_sent || 0}</span>
          <span className="pitch-stat-lbl">Intro Sent</span>
        </div>
        <div className="pitch-summary-stat" style={{ color: "#FFB347" }}>
          <span className="pitch-stat-val">{counts.in_progress || 0}</span>
          <span className="pitch-stat-lbl">In Progress</span>
        </div>
        <div className="pitch-summary-stat" style={{ color: "#A78BFA" }}>
          <span className="pitch-stat-val">{counts.submitted || 0}</span>
          <span className="pitch-stat-lbl">Submitted</span>
        </div>
        <div className="pitch-summary-stat" style={{ color: "#00DC78" }}>
          <span className="pitch-stat-val">{counts.approved || 0}</span>
          <span className="pitch-stat-lbl">Approved</span>
        </div>
      </div>

      <div className="mf-training-tip pitch-training-tip">
        <strong>How to use:</strong> Pick a target → click <em>Email Intro</em> to open your email client with the pitch pre-filled (auto BCC's hello@moovinforward.org for your audit trail), or <em>Copy</em> to paste into Gmail/Outlook manually. Update the status dropdown as you progress. All intro emails reflect the LABOR_TAX_STRATEGY positioning (ROI Stabilization Partner, Section 168(k) tax shield, scope-lock pricing).
      </div>

      {/* Tier Filter */}
      <div className="pitch-filter-row" data-testid="pitch-filter-row">
        {tiers.map(t => (
          <button
            key={t}
            onClick={() => setTierFilter(t)}
            className={`pitch-filter-btn ${tierFilter === t ? "active" : ""}`}
            data-testid={`pitch-filter-${t.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {t}
            <span className="pitch-filter-count">
              {t === "All" ? TARGETS.length : TARGETS.filter(x => x.tier === t).length}
            </span>
          </button>
        ))}
      </div>

      {/* Target Grid */}
      <div className="pitch-target-grid" data-testid="pitch-target-grid">
        {visible.map(target => {
          const currentStatus = statuses[target.id]?.status || "not_started";
          const statusMeta = STATUS_OPTIONS.find(s => s.key === currentStatus);
          return (
            <div key={target.id} className={`pitch-target-card pitch-status-${currentStatus}`} data-testid={`pitch-card-${target.id}`}>
              <div className="pitch-card-top">
                <div className="pitch-card-titles">
                  <span className={`pitch-priority pitch-priority-${target.priority}`}>{target.priority}</span>
                  <span className="pitch-tier-tag">{target.tier}</span>
                </div>
                <div className="pitch-status-pill" style={{ color: statusMeta.color, borderColor: statusMeta.color + "44", background: statusMeta.color + "11" }}>
                  {statusMeta.label}
                </div>
              </div>

              <h3 className="pitch-card-name">{target.name}</h3>
              <p className="pitch-card-subtitle">{target.subtitle}</p>

              <div className="pitch-card-detail">
                <strong>Contact:</strong> <span>{target.contact}</span>
              </div>
              <div className="pitch-card-detail">
                <strong>Opportunity:</strong> <span>{target.opportunity}</span>
              </div>
              <p className="pitch-card-note">{target.note}</p>

              <div className="pitch-card-actions">
                <button
                  onClick={() => openMailto(target)}
                  className="pitch-btn pitch-btn-primary"
                  data-testid={`pitch-email-${target.id}`}
                  title="Opens your email client with intro pitch pre-filled"
                >
                  ✉ Email Intro
                </button>
                <button
                  onClick={() => copyToClipboard(target)}
                  className="pitch-btn pitch-btn-secondary"
                  data-testid={`pitch-copy-${target.id}`}
                >
                  {copiedId === target.id ? "✓ Copied" : "Copy Pitch"}
                </button>
                <button
                  onClick={() => setActivePitch(activePitch === target.id ? null : target.id)}
                  className="pitch-btn pitch-btn-secondary"
                  data-testid={`pitch-preview-${target.id}`}
                >
                  {activePitch === target.id ? "Hide" : "Preview"}
                </button>
                <a
                  href={target.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pitch-btn pitch-btn-link"
                  data-testid={`pitch-portal-${target.id}`}
                >
                  Open Portal ↗
                </a>
              </div>

              <div className="pitch-card-status-row">
                <label htmlFor={`status-${target.id}`}>Update Status:</label>
                <select
                  id={`status-${target.id}`}
                  value={currentStatus}
                  onChange={(e) => updateStatus(target.id, e.target.value)}
                  className="pitch-status-select"
                  data-testid={`pitch-status-${target.id}`}
                >
                  {STATUS_OPTIONS.map(s => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </select>
              </div>

              {activePitch === target.id && (
                <pre className="pitch-preview-box" data-testid={`pitch-preview-text-${target.id}`}>
                  {generatePitch(target)}
                </pre>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PitchCenterTab;
